"use client";

import Link from "next/link";
import { useState, MouseEvent } from "react";
import { cn } from "@/utils/cn";

type Variant = "primary" | "secondary" | "danger";

type BaseProps = {
  label: string;
  className?: string;
  variant?: Variant;
  showIcon?: boolean;
};

type ButtonProps = BaseProps & {
  type?: "button" | "submit" | "reset";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  link?: never;
};

type LinkProps = BaseProps & {
  link: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  type?: never;
};

type ActionButtonProps = ButtonProps | LinkProps;

export default function ActionButton(props: ActionButtonProps) {
  const { label, className, showIcon = true } = props;
  const [hovered, setHovered] = useState(false);

  const sharedClass = cn(
    "btn-real-estate",
    className
  );

  const iconSvg = (
    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
    </svg>
  );

  return (
    <>
      <style>{`
        .btn-real-estate {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 2.2rem;
          font-family: 'Montserrat', sans-serif;
          font-weight: 500;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #DBB653;
          background: rgba(219, 182, 83, 0.05);
          border: 1px solid rgba(219, 182, 83, 0.5);
          border-radius: 9999px;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
          cursor: pointer;
          z-index: 1;
          text-decoration: none;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .btn-real-estate::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(120deg, #DBB653, #fbe8b5, #DBB653);
          background-size: 200% auto;
          z-index: -1;
          transform: scaleX(0);
          transform-origin: right;
          transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
        }

        .btn-real-estate:hover {
          color: #0b3848;
          border-color: transparent;
          font-weight: 600;
          box-shadow: 0 10px 25px -5px rgba(219, 182, 83, 0.4);
        }

        .btn-real-estate:hover::before {
          transform: scaleX(1);
          transform-origin: left;
          animation: gradient-shine-btn 2s linear infinite;
        }

        @keyframes gradient-shine-btn {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }

        .btn-icon {
          margin-left: 12px;
          width: 20px;
          height: 20px;
          transition: transform 0.3s ease;
        }

        .btn-real-estate:hover .btn-icon {
          transform: translateX(6px);
        }
      `}</style>

      {/* ---------- LINK ---------- */}
      {"link" in props ? (
        <Link
          href={props.link ?? "/"}
          onClick={props.onClick as ((e: MouseEvent<HTMLAnchorElement>) => void) | undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={sharedClass}
        >
          <span className="relative z-[2] whitespace-nowrap">{label}</span>
          {showIcon && iconSvg}
        </Link>
      ) : (
        /* ---------- BUTTON ---------- */
        <button
          type={props.type ?? "button"}
          onClick={props.onClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={sharedClass}
        >
          <span className="relative z-[2] whitespace-nowrap">{label}</span>
          {showIcon && iconSvg}
        </button>
      )}
    </>
  );
}
